import React, { useState } from "react";
import { Plus, Loader2, CalendarCheck, Mail } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Switch } from "../ui/switch";
import { reviewStatusColors, formatDate } from "./constants";

export function ReviewsTab({ contractId, reviews, fetchData, expanded, submitForm, updateItem }) {
  const [form, setForm] = useState({ title: "", review_date: "", description: "", notify_supplier: false, notify_owner: false });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await submitForm(
      `/contracts/${contractId}/review-dates`,
      form,
      () => setForm({ title: "", review_date: "", description: "", notify_supplier: false, notify_owner: false }),
      setShowForm
    );
    setSaving(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{reviews.length} review(s)</p>
        <Button size="sm" variant="outline" className="gap-1 rounded-sm" onClick={() => setShowForm(true)} data-testid="add-review-btn"><Plus size={14} />Schedule Review</Button>
      </div>

      {showForm && (
        <Card className="border-cyan-200">
          <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Review Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm h-9" placeholder="e.g., Quarterly Performance Review" data-testid="review-title-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Review Date</Label>
                <Input type="date" value={form.review_date} onChange={(e) => setForm({ ...form, review_date: e.target.value })} className="rounded-sm h-9" data-testid="review-date-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Description (optional)</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm h-9" placeholder="What should be reviewed?" />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.notify_supplier} onCheckedChange={(v) => setForm({ ...form, notify_supplier: v })} />
                <Label className="text-xs">Notify Supplier</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.notify_owner} onCheckedChange={(v) => setForm({ ...form, notify_owner: v })} />
                <Label className="text-xs">Notify Contract Owner</Label>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button size="sm" disabled={saving || !form.title || !form.review_date} onClick={handleSave} data-testid="save-review-btn">{saving ? <Loader2 size={14} className="animate-spin" /> : "Save"}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className={`space-y-2 overflow-y-auto ${expanded ? "max-h-[calc(100vh-350px)]" : "max-h-[400px]"}`}>
        {reviews.map((review) => (
          <div key={review.id} className="p-3 border rounded-sm">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <CalendarCheck size={14} className="text-slate-400" />
                <span className="font-medium text-sm">{review.title}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`rounded-sm text-xs ${reviewStatusColors[review.status]}`}>{review.status}</Badge>
                {review.status === "scheduled" && (
                  <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={() => updateItem(`/review-dates/${review.id}`, { status: "completed" })}>Mark Complete</Button>
                )}
              </div>
            </div>
            <p className="text-sm text-slate-600">{formatDate(review.review_date)}</p>
            {review.description && <p className="text-xs text-slate-500 mt-1">{review.description}</p>}
            <div className="flex gap-3 mt-1">
              {review.notify_supplier && <span className="text-xs text-blue-500 flex items-center gap-1"><Mail size={10} />Supplier notified</span>}
              {review.notify_owner && <span className="text-xs text-blue-500 flex items-center gap-1"><Mail size={10} />Owner notified</span>}
            </div>
          </div>
        ))}
        {reviews.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No reviews scheduled</p>}
      </div>
    </div>
  );
}
